/**
 * Rebuilding the tool pills and stats footer for a reloaded conversation.
 *
 * Both are React state keyed by message id, so a refresh empties them; the
 * information itself survives in `chat_messages.message_metadata` and is read
 * back here.
 *
 * The keying is the part worth being careful about. use-chat exports its
 * messages with the `db_` prefix stripped — historical ids are normalised to
 * blob-storage ids so the feedback endpoints resolve — and every surface looks
 * up by that exported id. Keying these maps by `db_<id>` therefore populated
 * them and guaranteed the lookups missed: a reloaded conversation showed no
 * pills and no token/timing footer, while the identical turn showed both while
 * live. Live worked by accident, because onFinish writes its entry under two
 * ids and one of them matched.
 *
 * So entries go in under both ids, which is what the live path already does.
 */
import type { ExecutedTool } from '@/types/chat'
import { isUserVisibleTool } from './tool-visibility'

/**
 * Shaped to match use-chat's messageStatsMap state exactly. `count` and `tools`
 * are required there, so a persisted tool_stats missing either is normalised
 * rather than widened — widening the state type would push the optionality out
 * to every consumer that renders these numbers.
 */
export interface RestoredStats {
  usage?: Record<string, number>
  tool_stats?: { count: number; tools: string[]; iterations?: number }
  elapsed_ms?: number
}

export interface RestoredMaps {
  tools: Record<string, ExecutedTool[]>
  stats: Record<string, RestoredStats>
}

interface SavedMessage {
  id?: unknown
  role?: unknown
  message_metadata?: {
    usage?: Record<string, number>
    tool_stats?: { count?: number; tools?: string[]; iterations?: number }
    tool_results?: Array<{ tool?: string }>
    elapsed_ms?: number
  }
}

/**
 * Names of the tools a saved turn ran.
 *
 * `tool_stats.tools` is the flat list the server records and is preferred: it
 * covers tools that produced no payload. `tool_results` only exists for tools
 * that returned something, so it is the fallback rather than the source.
 */
function toolNamesFrom(meta: NonNullable<SavedMessage['message_metadata']>): string[] {
  if (Array.isArray(meta.tool_stats?.tools)) return meta.tool_stats.tools
  if (Array.isArray(meta.tool_results)) {
    return meta.tool_results.map((entry) => entry?.tool).filter((name): name is string => !!name)
  }
  return []
}

export function buildRestoredMaps(saved: unknown): RestoredMaps {
  const tools: Record<string, ExecutedTool[]> = {}
  const stats: Record<string, RestoredStats> = {}
  if (!Array.isArray(saved)) return { tools, stats }

  for (const message of saved as SavedMessage[]) {
    if (message?.role !== 'assistant') continue
    const meta = message.message_metadata
    if (!meta) continue
    const id = typeof message.id === 'string' ? message.id : null
    if (!id) continue
    // Both spellings — see the note at the top of this file.
    const keys = [id, `db_${id}`]

    if (meta.usage || meta.tool_stats || meta.elapsed_ms) {
      const entry: RestoredStats = {}
      if (meta.usage) entry.usage = meta.usage
      if (meta.tool_stats) {
        entry.tool_stats = {
          ...meta.tool_stats,
          count: meta.tool_stats.count ?? 0,
          tools: meta.tool_stats.tools ?? [],
        }
      }
      if (meta.elapsed_ms) entry.elapsed_ms = meta.elapsed_ms
      for (const key of keys) stats[key] = entry
    }

    // Control signals are filtered here too, so a reloaded turn shows exactly
    // what the live one did. Only successes are persisted today, which is why
    // restored entries are all marked success.
    const names = toolNamesFrom(meta).filter(isUserVisibleTool)
    if (names.length > 0) {
      const executed = names.map((name) => ({ name, status: 'success' as const }))
      for (const key of keys) tools[key] = executed
    }
  }

  return { tools, stats }
}
